import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Checkbox, Form, Input, Modal, message
} from 'antd';
import { FormWrapper } from 'components/Page';
import { canViewOptimiser, canAdminOptimiser } from 'library/optimiser';

const FormItem = Form.Item;

const mapPropsToFields = (props) => {
  const { dataSource } = props;
  const {
    name
  } = dataSource;

  return {
    name: Form.createFormField({ value: name })
  };
};

const onFieldsChange = (props, fields) => {
  const { onFieldsChange: dispatchOnFieldsChange } = props;
  if (dispatchOnFieldsChange) {
    dispatchOnFieldsChange(fields);
  }
};

const onValuesChange = (props, values) => {
  const { onValuesChange: dispatchOnValuesChange } = props;
  if (dispatchOnValuesChange) {
    dispatchOnValuesChange(values);
  }
};

const FormInput = ({
  canAdmin, dataSource, inputFormItemLayout, checkboxFormItemLayout, form
}) => {
  const { getFieldDecorator } = form;
  const {
    name, used_by_optimiser, id
  } = dataSource;
  const readonly = !canAdmin;

  const optimiserEnabled = canViewOptimiser();

  const [optimiserChecked, setOptimiserChecked] = useState(used_by_optimiser || false);

  useEffect(() => {
    setOptimiserChecked(used_by_optimiser || false);
  }, [id, used_by_optimiser]);

  const handleOptimiserToggle = (e) => {
    const { checked } = e.target;

    if (checked) {
      // First check if another roster is already selected
      axios.get('/api/roster/meta/', { params: { used_by_optimiser: true } })
        .then((response) => {
          const data = response.data.toJS ? response.data.toJS() : response.data;
          const results = data.results || [];
          const currentOptimiser = results.find(r => r.id !== id);

          if (currentOptimiser) {
            Modal.confirm({
              title: 'Change Optimiser Roster',
              content: `Roster "${currentOptimiser.name}" is currently selected for Optimisation and only 1 roster can be selected at a time. Do you want to select Roster "${dataSource.name}" for optimisation instead of Roster "${currentOptimiser.name}"?`,
              okText: 'Yes',
              cancelText: 'No',
              onOk() {
                return axios.post(`/api/roster/meta/${id}/set_optimiser/`)
                  .then(() => {
                    setOptimiserChecked(true);
                    message.success(`Roster "${dataSource.name}" is now selected for Optimisation.`);
                  })
                  .catch((err) => {
                    message.error(err?.response?.data?.detail || 'Failed to set roster for Optimisation.');
                  });
              }
            });
          }
          else {
            // No other roster selected, set directly
            axios.post(`/api/roster/meta/${id}/set_optimiser/`)
              .then(() => {
                setOptimiserChecked(true);
                message.success(`Roster "${dataSource.name}" is now selected for Optimisation.`);
              })
              .catch((err) => {
                message.error(err?.response?.data?.detail || 'Failed to set roster for Optimisation.');
              });
          }
        });
    }
    else {
      Modal.confirm({
        title: 'Remove from Optimisation',
        content: `Are you sure you want to remove Roster "${dataSource.name}" from optimisation?`,
        okText: 'Yes',
        cancelText: 'No',
        onOk() {
          return axios.post(`/api/roster/meta/${id}/unset_optimiser/`)
            .then(() => {
              setOptimiserChecked(false);
              message.success(`Roster "${dataSource.name}" removed from Optimisation.`);
            })
            .catch(() => {
              message.error('Failed to remove roster from Optimisation.');
            });
        }
      });
    }
  };

  return (
    <Form>
      <FormItem {...inputFormItemLayout} label="Name">
        {getFieldDecorator('name', {
          initialValue: name,
          rules: [{
            required: true,
            message: 'Please enter the Roster name.'
          }]
        })(<Input disabled={readonly} />)}
      </FormItem>
      {optimiserEnabled && id && (
        <FormItem {...checkboxFormItemLayout || inputFormItemLayout}>
          <Checkbox
            checked={optimiserChecked}
            disabled={readonly || !canAdminOptimiser()}
            onChange={handleOptimiserToggle}
          >
            Used by Optimiser
          </Checkbox>
        </FormItem>
      )}
    </Form>
  );
};

FormInput.propTypes = {
  canAdmin: PropTypes.bool,
  checkboxFormItemLayout: PropTypes.object,
  dataSource: PropTypes.object,
  form: PropTypes.object,
  inputFormItemLayout: PropTypes.object,
  onFieldsChange: PropTypes.func,
  onValuesChange: PropTypes.func
};

FormInput.defaultProps = {
  canAdmin: false,
  checkboxFormItemLayout: {},
  dataSource: {},
  form: {},
  inputFormItemLayout: {},
  onFieldsChange() {
  },
  onValuesChange() {
  }
};

const RosterInput = FormWrapper(Form.create({
  mapPropsToFields,
  onFieldsChange,
  onValuesChange
})(FormInput));

export default RosterInput;
